import { useEffect, useState } from "react";
import { Megaphone, Play, Pause, Trash2, Check, RefreshCw, Inbox } from "lucide-react";
import api, { formatErr, formatTime } from "@/lib/api";

export default function VoiceDropInbox() {
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [audioEl, setAudioEl] = useState(null);
  const [error, setError] = useState("");

  const fetchDrops = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/voice-drops");
      setDrops(data);
      setError("");
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || "Couldn't load drops");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDrops(); }, []);

  const togglePlay = async (drop) => {
    if (playingId === drop.id) {
      audioEl?.pause();
      setPlayingId(null);
      return;
    }
    if (audioEl) audioEl.pause();

    const el = new Audio(drop.audio_url);
    el.onended = async () => {
      setPlayingId(null);
      if (drop.status === "new") {
        try { await api.patch(`/voice-drops/${drop.id}`, { status: "played" }); fetchDrops(); }
        catch (e) { /* ignore */ }
      }
    };
    el.onerror = () => { setError("Could not play this drop."); setPlayingId(null); };
    setAudioEl(el);
    setPlayingId(drop.id);
    try { await el.play(); } catch (e) { setError("Tap play once more."); setPlayingId(null); }
  };

  const handleDelete = async (drop) => {
    if (!window.confirm("Delete this voice drop?")) return;
    if (playingId === drop.id) { audioEl?.pause(); setPlayingId(null); }
    try {
      await api.delete(`/voice-drops/${drop.id}`);
      fetchDrops();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
    }
  };

  const handleMarkPlayed = async (drop) => {
    try {
      await api.patch(`/voice-drops/${drop.id}`, { status: drop.status === "new" ? "played" : "new" });
      fetchDrops();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
    }
  };

  const unread = drops.filter((d) => d.status === "new").length;

  return (
    <section className="admin-card p-6 mt-8" data-testid="voice-drop-inbox">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Megaphone size={16} className="text-[var(--brand-purple-light)]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--brand-purple-light)]">
              Studio Line
            </span>
            {unread > 0 && (
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[var(--neon-cyan)] text-black font-bold" data-testid="unread-badge">
                {unread} NEW
              </span>
            )}
          </div>
          <h2 className="font-display text-xl font-bold">Voice Drops Inbox</h2>
        </div>
        <button onClick={fetchDrops} className="btn-ghost flex items-center gap-1.5" data-testid="refresh-drops-button">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {error && <p className="text-[#ff5566] text-sm font-mono mb-3" data-testid="drop-error">{error}</p>}

      {loading && drops.length === 0 ? (
        <p className="text-[var(--text-muted)] text-sm font-mono py-6 text-center">Loading inbox...</p>
      ) : drops.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[var(--border-soft)] rounded-lg" data-testid="empty-drops">
          <Inbox size={32} className="mx-auto text-[var(--text-muted)] mb-3 opacity-50" />
          <p className="text-[var(--text-muted)] text-sm">No voice drops yet.</p>
          <p className="text-[var(--text-muted)] text-xs font-mono mt-1">
            Listeners can send shout-outs from the homepage.
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="drop-list">
          {drops.map((d) => {
            const isNew = d.status === "new";
            const isPlaying = playingId === d.id;
            return (
              <li
                key={d.id}
                data-testid={`drop-row-${d.id}`}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  isNew
                    ? "bg-[rgba(0,242,254,0.04)] border-[var(--border-cyan)]"
                    : "bg-[#0d0d16] border-[var(--border-soft)]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => togglePlay(d)}
                  data-testid={`drop-play-${d.id}`}
                  className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00f2fe] to-[#00a8ff] text-black flex items-center justify-center flex-shrink-0 hover:scale-105 transition-transform"
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" style={{ marginLeft: 2 }} />}
                </button>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate text-white">
                    {d.listener_label || <span className="italic text-[var(--text-muted)]">Anonymous listener</span>}
                  </p>
                  <p className="text-xs text-[var(--text-muted)] font-mono">
                    {new Date(d.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                    {d.duration > 0 && ` · ${formatTime(d.duration)}`}
                    {isNew && <span className="ml-2 text-[var(--neon-cyan)]">● NEW</span>}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => handleMarkPlayed(d)}
                  className="btn-ghost !py-1 !px-2 flex items-center gap-1"
                  data-testid={`drop-toggle-status-${d.id}`}
                  title={isNew ? "Mark as played" : "Mark as new"}
                >
                  <Check size={12} />
                </button>

                <button
                  type="button"
                  onClick={() => handleDelete(d)}
                  className="btn-danger flex items-center gap-1"
                  data-testid={`drop-delete-${d.id}`}
                  aria-label="Delete"
                >
                  <Trash2 size={12} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
